/**
 * ランク色トークン（設計書 付録C 実測値）
 *
 * §8-1 / §F.3: サーバの `skill_rank_tiers.color` が「単一の真実」であり、
 * フロント定数は表示フォールバック / モック専用。
 * 共有パッケージが無いため各リポジトリに複製する（HR フロントにも同名ファイルを置く）。
 *
 * tier オブジェクトの `color` が来ない / 未知 code の場合のみ本テーブルへフォールバックする。
 */

import { RankTierCode } from '../types';

// C.1 ランクティア星（skill_rank_tiers.color）— 代表フィル + ハイライト
export interface RankTierColor {
  /** 代表フィル（seed 値）。バッジ/星の主色。 */
  main: string;
  /** 明るいハイライト（2-stop グラデの上側）。 */
  highlight: string;
  /** 日本語表示名（サーバ name が来ない時のフォールバック）。 */
  label: string;
}

export const RANK_TIER_COLORS: Record<RankTierCode, RankTierColor> = {
  rookie:   { main: '#57A665', highlight: '#90D59B', label: 'ルーキー' },   // 緑
  bronze:   { main: '#B7553C', highlight: '#FFC490', label: 'ブロンズ' },   // オレンジ茶
  silver:   { main: '#BDC2C4', highlight: '#D8DCDE', label: 'シルバー' },   // 灰（低彩度）
  gold:     { main: '#EEB812', highlight: '#F5CE3A', label: 'ゴールド' },   // 金
  platinum: { main: '#8095A8', highlight: '#E8F0F8', label: 'プラチナ' },   // 冷色系メタリック
};

// sort_order 昇順のティア code（ルーキー → プラチナ）。フォールバック比較等に使用。
export const RANK_TIER_ORDER: RankTierCode[] = ['rookie', 'bronze', 'silver', 'gold', 'platinum'];

// C.2 アクセント / UI
export const ACCENT_COLORS = {
  primaryBlue: '#0071E3',       // 数値・リンク・アクティブタブ
  sendFabBlue: '#0070E0',       // 送信FAB（紙飛行機）
  copilotPurple: '#6A3DC2',     // 育成アドバイス紫テキスト/アイコン
  copilotPill: '#534DFF',       // COPILOT ピル背景
  adviceBadge: '#673FC8',       // 育成アドバイスバッジ（丸）
  copilotCardBorder: '#EEEDFF', // COPILOT カード枠（淡ラベンダー）
  selectedCardBg: '#CCE3F9',    // 選択カード背景（淡青ハイライト）
  alertRed: '#E83828',          // 信頼スコア / アラート赤
} as const;

// C.3 バー・ボタングラデーション（橙→赤）
export const GRADIENT_ORANGE_RED = {
  from: '#F18F03', // 橙（左）
  to: '#E84024',   // 赤（右）
  track: '#C4C4C4', // 未充填トラック
} as const;

// C.4 スキルパラメーター レーダー
export const RADAR_COLORS = {
  fill: '#5BA4E8',   // 塗り（ミディアムブルー）
  stroke: '#0071E3', // 枠線（プライマリ青系）
  grid: '#E0E0E0',   // グリッド線（淡灰）
} as const;

// C.5 演出画面の背景グラデーション（ENTRY SENT / INVITATION / GET SKILL）
export const STAGE_GRADIENT = {
  topFrom: '#554BF8',    // 上側（青紫）
  topTo: '#6043DA',
  bottomFrom: '#7039B2', // 下側（マゼンタ紫）
  bottomTo: '#7B3294',
  accentFrom: '#F18F03', // アクセントアイコン 橙〜金
  accentTo: '#EEB812',
} as const;

/**
 * tier code から色を解決（サーバ color を優先、無ければ付録C にフォールバック）。
 */
export const resolveTierColor = (code: string | undefined, serverColor?: string | null): string => {
  if (serverColor && /^#[0-9A-Fa-f]{6}$/.test(serverColor)) return serverColor;
  const c = (code ?? '') as RankTierCode;
  return RANK_TIER_COLORS[c]?.main ?? RANK_TIER_COLORS.rookie.main;
};

/**
 * tier code からハイライト色を解決（グラデ上側）。
 */
export const resolveTierHighlight = (code: string | undefined): string => {
  const c = (code ?? '') as RankTierCode;
  return RANK_TIER_COLORS[c]?.highlight ?? RANK_TIER_COLORS.rookie.highlight;
};
